export interface FirebaseUserSnapshot {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  isAnonymous: boolean;
  providerData: Array<{
    providerId: string;
    uid: string;
    email: string | null;
  }>;
}

function claimString(value: unknown): string {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => claimString(item)).join(',');
  }
  return '';
}

export function decodeJwtPayload(
  token: string
): Record<string, unknown> | null {
  const segment = token.split('.')[1];
  if (!segment) {
    return null;
  }
  try {
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '='
    );
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function describeGoogleIdToken(
  googleIdToken: string
): Record<string, string> {
  if (!googleIdToken) {
    return { googleCredential: 'empty' };
  }
  const payload = decodeJwtPayload(googleIdToken);
  if (!payload) {
    return { googleCredential: 'undecodable' };
  }
  return {
    googleEmail: claimString(payload.email),
    googleEmailVerified: claimString(payload.email_verified),
    googleSub: claimString(payload.sub),
    googleHd: claimString(payload.hd),
    googleAud: claimString(payload.aud),
    googleIss: claimString(payload.iss),
    googleIat: claimString(payload.iat),
    googleExp: claimString(payload.exp),
  };
}

export function describeFirebaseUser(
  user: FirebaseUserSnapshot | null,
  prefix: string
): Record<string, string> {
  if (!user) {
    return { [`${prefix}User`]: 'null' };
  }
  const googleProvider = user.providerData.find(
    (provider) => provider.providerId === 'google.com'
  );
  const providers = user.providerData.map((provider) => ({
    providerId: provider.providerId,
    uid: provider.uid,
    email: provider.email ?? '',
  }));
  return {
    [`${prefix}Uid`]: user.uid,
    [`${prefix}Email`]: user.email ?? '',
    [`${prefix}EmailVerified`]: String(user.emailVerified),
    [`${prefix}IsAnonymous`]: String(user.isAnonymous),
    [`${prefix}HasGoogleProvider`]: String(Boolean(googleProvider)),
    [`${prefix}GoogleProviderUid`]: googleProvider?.uid ?? '',
    [`${prefix}GoogleProviderEmail`]: googleProvider?.email ?? '',
    [`${prefix}Providers`]: JSON.stringify(providers),
  };
}

export function describeAuthMismatch(
  beforeUser: FirebaseUserSnapshot | null,
  googleIdToken: string
): Record<string, string> {
  const payload = googleIdToken ? decodeJwtPayload(googleIdToken) : null;
  const googleEmail = typeof payload?.email === 'string' ? payload.email : '';
  const googleSub = typeof payload?.sub === 'string' ? payload.sub : '';
  const googleProvider = beforeUser?.providerData.find(
    (provider) => provider.providerId === 'google.com'
  );

  return {
    beforeUserPresent: String(Boolean(beforeUser)),
    emailsMatchIgnoringCase:
      beforeUser?.email && googleEmail
        ? String(beforeUser.email.toLowerCase() === googleEmail.toLowerCase())
        : '',
    googleSubMatchesLinkedProviderUid: googleProvider
      ? String(googleProvider.uid === googleSub)
      : '',
    googleEmailMatchesLinkedProviderEmail:
      googleProvider?.email && googleEmail
        ? String(
            googleProvider.email.toLowerCase() === googleEmail.toLowerCase()
          )
        : '',
  };
}

const TOKEN_FIELD_NAMES = new Set([
  'idToken',
  'id_token',
  'oauthIdToken',
  'oauthAccessToken',
  'accessToken',
  'access_token',
  'refreshToken',
  'pendingToken',
  'rawUserInfo',
]);

export function sanitizedFirebaseCustomData(
  customData: Record<string, unknown> | undefined
): Record<string, string> {
  if (!customData) {
    return {};
  }
  const details: Record<string, string> = {};
  if (typeof customData.email === 'string') {
    details.firebaseCustomDataEmail = customData.email;
  }
  if (typeof customData.appName === 'string') {
    details.firebaseAppName = customData.appName;
  }

  const tokenResponse = customData._tokenResponse;
  if (!tokenResponse || typeof tokenResponse !== 'object') {
    return details;
  }
  const response = tokenResponse as Record<string, unknown>;
  const nestedError = response.error;
  const nestedMessage =
    nestedError && typeof nestedError === 'object'
      ? (nestedError as { message?: unknown }).message
      : undefined;
  const serverError = response.errorMessage ?? nestedMessage;
  if (typeof serverError === 'string') {
    details.firebaseServerError = serverError;
  }
  if (typeof response.email === 'string') {
    details.firebaseServerEmail = response.email;
  }
  if (typeof response.localId === 'string') {
    details.firebaseServerLocalId = response.localId;
  }
  if (typeof response.federatedId === 'string') {
    details.firebaseFederatedId = response.federatedId;
  }
  if (typeof response.providerId === 'string') {
    details.firebaseResponseProviderId = response.providerId;
  }

  const leakedTokenFields = Object.keys(response).filter((key) =>
    TOKEN_FIELD_NAMES.has(key)
  );
  if (leakedTokenFields.length > 0) {
    details.firebaseOmittedTokenFields = leakedTokenFields.join(',');
  }
  return details;
}
